import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddGameCard from './AddGameCard';
import GameCard from './GameCard';
import { fetchCompanionGames, fetchShelfGames } from '../../api/library';
import ReportIssueButton from '../support/ReportIssueButton';
import { supportContextFromApiError } from '../../utils/supportReports';

const EMPTY_FILTERS = Object.freeze({});

const ShelfSection = ({
  limit = 5,
  search = '',
  filters = EMPTY_FILTERS,
  sort = 'recent',
  source = 'mine',
  onAdd,
  onSelect,
  onTotalChange,
}) => {
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState('');
  const [errorContext, setErrorContext] = useState(null);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const showPagination = totalPages > 1;

  useEffect(() => {
    setPage(1);
  }, [filters, search, sort, source]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const request =
      source === 'friends'
        ? fetchCompanionGames({ limit, page, search, ...filters, sort })
        : fetchShelfGames({ limit, page, search, ...filters, sort });

    request
      .then((data) => {
        if (!mounted) return;
        const nextTotal = data.total ?? data.items?.length ?? 0;
        setGames(data.items || []);
        setTotal(nextTotal);
        setHasMore(Boolean(data.hasMore));
        setErrorKey('');
        setErrorContext(null);
        if (source !== 'friends') {
          onTotalChange?.(nextTotal);
        }
      })
      .catch((error) => {
        if (!mounted) return;
        setErrorKey('shelf.feedback.loadError');
        setErrorContext(
          supportContextFromApiError(error, {
            pageName: source === 'friends' ? 'Companion Shelf' : 'Game Shelf',
          })
        );
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [filters, limit, onTotalChange, page, search, sort, source]);

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={260} />
            <Stack direction="row" spacing={2}>
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="22%" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (errorKey) {
    return (
      <Alert
        severity="error"
        action={<ReportIssueButton context={errorContext} sx={{ whiteSpace: 'nowrap' }} />}
      >
        {t(errorKey)}
      </Alert>
    );
  }

  const hasActiveQuery = Boolean(search) || Object.values(filters).some(Boolean);

  if (source === 'friends' && games.length === 0 && !hasActiveQuery) {
    return (
      <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
        <CardContent>
          <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h3">{t('shelf.friendsEmpty.title')}</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 460 }}>
              {t('shelf.friendsEmpty.subtitle')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (games.length === 0) {
    return !hasActiveQuery && source !== 'friends' ? (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: { xs: 2.5, md: 3 },
        }}
      >
        <AddGameCard onClick={onAdd} empty />
      </Box>
    ) : (
      <Alert severity="info" variant="outlined">
        {t('shelf.feedback.noSearchResults')}
      </Alert>
    );
  }

  const showAddCard = source !== 'friends' && page === 1;

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: { xs: 2.5, md: 3 },
        }}
      >
        {showAddCard ? <AddGameCard onClick={onAdd} /> : null}
        {games.map((game) => (
          <GameCard
            key={game.id}
            game={game}
            ownerName={source === 'friends' ? game.ownerName : undefined}
            onClick={onSelect}
          />
        ))}
      </Box>
      {showPagination ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button disabled={page === 1} onClick={() => setPage((current) => current - 1)}>
            {t('common.previous')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {t('common.page', { page, total: totalPages })}
          </Typography>
          <Button disabled={!hasMore} onClick={() => setPage((current) => current + 1)}>
            {t('common.next')}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ShelfSection;
